import React from 'react';
import * as Sentry from '@sentry/browser';
import wix from '../../services/wix';
import _isEmpty from 'lodash/isEmpty';
import {
  EDITOR_VIEW_MODE,
  EditorViewMode,
} from '../../constants/editor-view-mode';
import { WINDOW_POSITION } from '../../constants/editor-settings-options';
import { IMAGE_PREVIEW_MODAL_SIZE } from '../../constants/image-preview';
import BusinessOnlyWidget from '../Widgets/BusinessOnlyWidget';
import styles from './App.scss';
import classNames from 'classnames';
import {
  ChatConfigurationProvider,
  expandCollapseManager,
  titleManagerFactory,
} from '@wix/chat-web';
import { withExperiments } from '@wix/wix-experiments-react';
import {
  updateStyleParamsColors,
  updateSiteColors,
  updateEditorSettingsDesign,
  updateEditorSettingsLayout,
  updateEditorSettingsTexts,
  updateIsChatSdkInit,
  expandWidget,
  updateStyleParamsFonts,
} from '../../actions';
import { connect } from 'react-redux';
import {
  primaryColorOrDefault,
  textColorOrDefault,
  shouldShowChat,
  isExperimentOn,
  messagesFontFamily,
} from '../../reducers';
import * as biLogger from '../../services/biLogger';
import { InjectedTranslateProps, translate } from 'react-i18next';
import { IMAGE_PREVIEW_PAGE } from '../../constants/endpoints';
import { Experiments } from '../../constants/experiments';
import { fedopsLogger, LoadingPhase } from '../../services/fedopsLogger';
import { loadingScheduler } from '../../services/loading-scheduler';

export interface SiteAppProps extends InjectedTranslateProps {
  locale: string;
  experiments: any;
  styleParamsColorsChange: Function;
  styleParamsFontsChange: Function;
  siteColorsChange: Function;
  editorSettingsDesignChange: Function;
  editorSettingsLayoutChange: Function;
  editorSettingsTextsChange: Function;
  updateIsChatSdkInit: Function;
  expandWidget: Function;
  messagesFontFamily: string;
  primaryColor: string;
  textColor: string;
  editorSettingsDesign: any;
  editorSettingsLayout: any;
  editorSettingsTexts: any;
  viewMode: EditorViewMode;
  shouldShowChat: boolean;
  businessChatEnabled: boolean;
  deviceType: string;
  isLeadCaptureFormV2ExperimentOn: boolean;
  allowInput: boolean;
  isChangePageTitleExperimentOn: boolean;
}

export interface SiteAppState {
  disableAutoMessages: boolean;
}

class SiteApp extends React.Component<SiteAppProps, SiteAppState> {
  pageTitleManager;

  constructor(props) {
    super(props);

    const urlParams = new URLSearchParams(window.location.search);
    const disableAutoMessages = urlParams.has('disableAutoMessages');

    this.state = {
      disableAutoMessages,
    };

    this.initBiLogger();
  }

  initBiLogger = () => {
    biLogger.initLogger({
      instanceId: wix.getInstanceId(),
      biToken: wix.getBiToken(),
      visitorId: wix.getAid(),
      viewMode: wix.getViewMode(),
      isSocial: false,
      isBusiness: this.props.businessChatEnabled,
    });
  };

  initTitleManager = () => {
    const {
      deviceType,
      viewMode,
      locale,
      isChangePageTitleExperimentOn,
    } = this.props;

    if (
      isChangePageTitleExperimentOn &&
      deviceType === 'desktop' &&
      viewMode === EDITOR_VIEW_MODE.SITE
    ) {
      const setPageTitleChangeCb = title => wix.setBrowserTabTitle({ title });
      this.pageTitleManager = titleManagerFactory.createTitleManager({
        setTitleCb: setPageTitleChangeCb,
        defaultTitle: '',
        locale,
      });
    }
  };

  async loadSettings() {
    const publicData = this.loadPublicData();

    const styleParams = Promise.all([
      this.populateColorsFromWix(),
      this.populateFontsFromWix(),
    ]);

    const { primaryColor, textColor } = this.props;

    if (!primaryColor || !textColor) {
      await styleParams;
    }

    void Promise.all([publicData, styleParams]).then(() => {
      fedopsLogger.appLoadingPhaseStart(LoadingPhase.ReceivedSettingsAndStyles);
    });
  }

  async componentDidMount() {
    this.initTitleManager();
    wix.subscribeToExpandWidgetRequest(expandCollapseManager.expand);

    try {
      await this.loadSettings();

      void this.loadPlugins();
      this.showQabIfNeeded();

      wix.reportApplicationLoaded();
      fedopsLogger.appLoaded();
    } catch (e) {
      Sentry.captureException(e);
    }
  }

  loadPlugins() {
    return loadingScheduler.onInteractive().then(async () => {
      (await import(/* webpackChunkName: "chat-interactivity" */ '../../services/plugins-loader')).loadPlugins();
    });
  }

  async populateFontsFromWix() {
    const { fonts } = await wix.getStyleParams();
    this.props.styleParamsFontsChange(fonts);
  }

  async populateColorsFromWix() {
    const { colors } = await wix.getStyleParams();
    this.props.styleParamsColorsChange(colors);

    const siteColors = await wix.getSiteColors();
    this.props.siteColorsChange(siteColors);
  }

  componentWillUnmount() {
    this.pageTitleManager && this.pageTitleManager.stop();
  }

  showQabIfNeeded() {
    if (this.props.deviceType === 'mobile' && this.props.shouldShowChat) {
      wix.updateQab({
        visible: true,
        notifications: false,
        color: this.props.primaryColor,
      });
    }
  }

  checkCollapseFromStyleParams = async () => {
    const { booleans } = await wix.getStyleParams();
    if (booleans.collapsedAsRounded) {
      const layout = {
        ...this.props.editorSettingsLayout,
        collapsedAsRounded: true,
      };
      this.props.editorSettingsLayoutChange(layout);
    }
  };

  loadPublicData = () => {
    const text = this.loadTabSettings('texts');
    const layout = this.loadTabSettings('layout').then(() =>
      this.checkCollapseFromStyleParams(),
    );
    const design = this.loadTabSettings('design');

    return Promise.all([text, layout, design]);
  };

  loadTabSettings = tab => wix.getPublicData(tab, this.handlePublicDataChanged);

  handlePublicDataChanged = publicData => {
    publicData.design &&
      this.props.editorSettingsDesignChange(publicData.design);
    publicData.layout &&
      this.props.editorSettingsLayoutChange(publicData.layout);
    publicData.texts && this.props.editorSettingsTextsChange(publicData.texts);
  };

  appClasses = () =>
    classNames({
      [styles.app]: true,
      [styles.mobile]: this.props.deviceType === 'mobile',
      [styles.left]:
        this.props.editorSettingsLayout.position ===
        WINDOW_POSITION.BOTTOM_LEFT,
    });

  handleImageClick = ({ url, width, height, fileName, fileSize }) => {
    const { primaryColor, textColor, deviceType } = this.props;
    const isDesktop = deviceType === 'desktop';

    biLogger.imageClickEvent();

    wix.openModal(
      `${window.location.origin}${IMAGE_PREVIEW_PAGE}?url=${encodeURIComponent(
        url,
      )}&fileName=${encodeURIComponent(
        fileName,
      )}&fileSize=${fileSize}&buttonColor=${encodeURIComponent(
        primaryColor,
      )}&textColor=${encodeURIComponent(
        textColor,
      )}&fontFamily=${encodeURIComponent(this.props.messagesFontFamily)}`,
      isDesktop ? IMAGE_PREVIEW_MODAL_SIZE.width : undefined,
      isDesktop ? IMAGE_PREVIEW_MODAL_SIZE.height : undefined,
    );
  };

  render() {
    const {
      editorSettingsDesign,
      editorSettingsLayout,
      editorSettingsTexts,
      isLeadCaptureFormV2ExperimentOn,
      allowInput,
    } = this.props;

    const primaryColor = this.props.primaryColor;
    const textColor = this.props.textColor;

    if (
      _isEmpty(editorSettingsTexts) ||
      _isEmpty(editorSettingsDesign) ||
      _isEmpty(editorSettingsLayout) ||
      !primaryColor ||
      !textColor
    ) {
      return null;
    }

    return (
      <ChatConfigurationProvider
        onImageMessageClick={this.handleImageClick as any}
        allowInput={isLeadCaptureFormV2ExperimentOn ? allowInput : true}
        disableAutoMessages={this.state.disableAutoMessages}
      >
        <div className={this.appClasses()}>
          <BusinessOnlyWidget />
        </div>
      </ChatConfigurationProvider>
    );
  }
}

const mapDispatchToProps = dispatch => {
  return {
    styleParamsColorsChange: colors =>
      dispatch(updateStyleParamsColors(colors)),
    styleParamsFontsChange: fonts => dispatch(updateStyleParamsFonts(fonts)),
    siteColorsChange: color => dispatch(updateSiteColors(color)),
    editorSettingsDesignChange: design =>
      dispatch(updateEditorSettingsDesign(design)),
    editorSettingsLayoutChange: layout =>
      dispatch(updateEditorSettingsLayout(layout)),
    editorSettingsTextsChange: texts =>
      dispatch(updateEditorSettingsTexts(texts)),
    updateIsChatSdkInit: data => dispatch(updateIsChatSdkInit(data)),
    expandWidget: () => dispatch(expandWidget()),
  };
};

function mapStateToProps(state) {
  return {
    messagesFontFamily: messagesFontFamily(state),
    primaryColor: primaryColorOrDefault(state),
    textColor: textColorOrDefault(state),
    editorSettingsDesign: state.editorSettingsDesign,
    editorSettingsLayout: state.editorSettingsLayout,
    editorSettingsTexts: state.editorSettingsTexts,
    viewMode: state.viewMode,
    locale: state.locale,
    deviceType: state.deviceType,
    shouldShowChat: shouldShowChat(state),
    businessChatEnabled: state.businessChatEnabled,
    isLeadCaptureFormV2ExperimentOn: isExperimentOn(
      state,
      'specs.chat.LeadCaptureFormV2',
    ),
    allowInput: state.allowInput,
    isChangePageTitleExperimentOn: isExperimentOn(
      state,
      Experiments.ChangePageTitle,
    ),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withExperiments(translate()(SiteApp)));
